import { ref } from 'vue'
import SensorResource from '@/api/resources/sensor'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { ISensor } from '@/interfaces/entities/sensor'
import { SensorType } from '@/interfaces/enums'
import { useI18n } from '@/composable/useI18n'
import { getWidgetSensorId, isGroupWidget } from '@/utils/dashboard/widget-source'
import {
  getLastDataAgeLabel,
  getLastPointFromGroupedReadings,
  parseLastReadingAt,
  shouldShowStaleDataNotice,
} from '@/utils/sensor/last-communication'

export interface ISensorLastReadingState {
  lastReadingAt: number | null
  error: string | null
}

export interface IWidgetStaleDataNoticeProps {
  message: string
  error: string | null
}

export function widgetNeedsLastCommunicationNotice(widget: IWidget): boolean {
  if (isGroupWidget(widget)) return false
  if (widget.widget_type === 'gauge' || widget.widget_type === 'status-panel') return true
  if (widget.widget_type === 'indicator') {
    return (widget.config.aggregation || 'mean') === 'last'
  }
  return false
}

export const LAST_READINGS_CACHE_TTL = 2 * 60 * 1000 // 2 minutes

const lastReadingFetchCache = new Map<string, { state: ISensorLastReadingState; fetchedAt: number }>()
const inFlightLastReadingFetches = new Map<string, Promise<ISensorLastReadingState>>()

export function clearLastReadingFetchCache(): void {
  lastReadingFetchCache.clear()
  inFlightLastReadingFetches.clear()
}

export function useDashboardSensorLastReadings() {
  const { t } = useI18n()

  const sensorLastReadings = ref<Record<string, ISensorLastReadingState>>({})
  const nowTimestamp = ref(Date.now())
  let ageTicker: ReturnType<typeof setInterval> | null = null

  function startAgeTicker() {
    stopAgeTicker()
    ageTicker = setInterval(() => {
      nowTimestamp.value = Date.now()
    }, 1000)
  }

  function stopAgeTicker() {
    if (ageTicker) {
      clearInterval(ageTicker)
      ageTicker = null
    }
  }

  async function fetchLastReadingFromApi(sensor: ISensor): Promise<ISensorLastReadingState> {
    const sensorId = sensor.id

    try {
      if (sensor.sensor_type === SensorType.ELECTRICITY) {
        const metrics = await SensorResource.getInfluxEnergyMetrics(sensorId)
        return {
          lastReadingAt: parseLastReadingAt(metrics?.last_reading_at),
          error: null,
        }
      }

      if (sensor.sensor_type === SensorType.TEMPERATURE) {
        const result = await SensorResource.getTemperatureInfluxLastReading(sensorId)
        const lastPoint = getLastPointFromGroupedReadings(result?.readings)
        const iso = result?.last_reading_at ?? lastPoint?.t
        return {
          lastReadingAt: parseLastReadingAt(iso),
          error: null,
        }
      }

      if (sensor.sensor_type === SensorType.DOOR_OPEN) {
        const result = await SensorResource.getOpenInfluxLastReading(sensorId)
        const lastPoint = getLastPointFromGroupedReadings(result?.readings)
        const iso = result?.last_reading_at ?? lastPoint?.t
        return {
          lastReadingAt: parseLastReadingAt(iso),
          error: null,
        }
      }

      return { lastReadingAt: null, error: null }
    } catch {
      return {
        lastReadingAt: null,
        error: t('custom_dashboards.view.stale_data_notice.fetch_error'),
      }
    }
  }

  async function fetchLastReadingForSensor(
    sensor: ISensor,
    options?: { force: boolean },
  ): Promise<ISensorLastReadingState> {
    const sensorKey = String(sensor.id)
    const now = Date.now()

    if (!options?.force) {
      const cached = lastReadingFetchCache.get(sensorKey)
      if (cached && now - cached.fetchedAt < LAST_READINGS_CACHE_TTL) {
        return cached.state
      }

      const inFlight = inFlightLastReadingFetches.get(sensorKey)
      if (inFlight) return inFlight
    }

    const fetchPromise = (async () => {
      const state = await fetchLastReadingFromApi(sensor)
      lastReadingFetchCache.set(sensorKey, { state, fetchedAt: Date.now() })
      return state
    })()

    if (!options?.force) {
      inFlightLastReadingFetches.set(sensorKey, fetchPromise)
    }

    try {
      return await fetchPromise
    } finally {
      inFlightLastReadingFetches.delete(sensorKey)
    }
  }

  function collectEligibleSensorIds(widgets: IWidget[]): string[] {
    const ids = new Set<string>()
    for (const widget of widgets) {
      if (!widgetNeedsLastCommunicationNotice(widget)) continue
      const sensorId = getWidgetSensorId(widget)
      if (sensorId) ids.add(sensorId)
    }
    return [...ids]
  }

  async function refreshLastReadings(
    widgets: IWidget[],
    sensors: ISensor[],
    options?: { force: boolean },
  ): Promise<void> {
    const sensorIds = collectEligibleSensorIds(widgets)
    if (sensorIds.length === 0) return

    const sensorById = new Map(sensors.map((s) => [String(s.id), s]))
    const results = await Promise.all(
      sensorIds.map(async (sensorId) => {
        const sensor = sensorById.get(sensorId)
        if (!sensor) {
          return [sensorId, { lastReadingAt: null, error: null }] as const
        }
        const state = await fetchLastReadingForSensor(sensor, options)
        return [sensorId, state] as const
      }),
    )

    const next: Record<string, ISensorLastReadingState> = { ...sensorLastReadings.value }
    for (const [sensorId, state] of results) {
      next[sensorId] = state
    }
    sensorLastReadings.value = next
  }

  function getNoticeProps(widget: IWidget): IWidgetStaleDataNoticeProps | null {
    if (!widgetNeedsLastCommunicationNotice(widget)) return null

    const sensorId = getWidgetSensorId(widget)
    if (!sensorId) return null

    const state = sensorLastReadings.value[String(sensorId)]
    if (!state) return null

    if (state.error) {
      return { message: '', error: state.error }
    }

    const noRecentLabel = t('custom_dashboards.view.stale_data_notice.no_recent_reading')
    const lastReadingAt = state.lastReadingAt

    if (!shouldShowStaleDataNotice(lastReadingAt, nowTimestamp.value)) {
      return null
    }

    if (!lastReadingAt) {
      return {
        message: t('custom_dashboards.view.stale_data_notice.stale_reading', {
          age: noRecentLabel,
        }),
        error: null,
      }
    }

    const ageLabel = getLastDataAgeLabel(lastReadingAt, nowTimestamp.value, noRecentLabel)

    return {
      message: t('custom_dashboards.view.stale_data_notice.stale_reading', {
        age: ageLabel,
      }),
      error: null,
    }
  }

  return {
    sensorLastReadings,
    widgetNeedsLastCommunicationNotice,
    refreshLastReadings,
    getNoticeProps,
    startAgeTicker,
    stopAgeTicker,
  }
}

export type DashboardSensorLastReadingsContext = ReturnType<typeof useDashboardSensorLastReadings>
